import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import Hammer from 'hammerjs';
import { useLocale } from '../contexts/LocaleContext';

interface MuseumMapSVGProps {
  exhibits: any[];
  currentTrack: number;
  selectExhibit: (index: number) => void;
  visitedExhibits: Set<number>;
  selectedExhibit: any;
  isDragging?: boolean;
  bottomOverlayHeight?: number;
  isNavigationPressed?: boolean;
  setIsNavigationPressed?: (value: boolean) => void;
  resetUserInteraction?: React.MutableRefObject<(() => void) | null>;
}

interface ViewState {
  x: number;
  y: number;
  k: number;
}

const MuseumMapSVG: React.FC<MuseumMapSVGProps> = ({
  exhibits,
  currentTrack,
  selectExhibit,
  visitedExhibits,
  selectedExhibit,
  bottomOverlayHeight = 0,
  isNavigationPressed = false,
  setIsNavigationPressed,
  resetUserInteraction,
}) => {
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const hammerRef = useRef<HammerManager | null>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialCentered = useRef(false);
  const isTransitioning = useRef(false);
  const centeredOnExhibit = useRef(false);

  // Smooth dragging state
  const [viewState, setViewState] = useState<ViewState>({ x: 0, y: 0, k: 1 });
  const isDragging = useRef(false);
  const lastPointerPos = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(0);

  const MIN_K = 0.5;
  const MAX_K = 3;
  const DEFAULT_ZOOM = 1.7; // ← pick your starting zoom


  // near your state
  const viewRef = useRef(viewState);
  const getCurrentView = () => viewRef.current;

  useEffect(() => { viewRef.current = viewState; }, [viewState]);


  // SVG viewBox from your original map.svg
  const SVG_VIEWBOX = { width: 841.89, height: 1190.55 };

  // Apply smooth transform to SVG
  const applyTransform = useCallback((transform: ViewState) => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const g = svg.select('g.map-container');

    if (g.node()) {
      g.attr('transform', `translate(${transform.x},${transform.y}) scale(${transform.k})`);
    }
  }, []);

  // Smooth momentum-based dragging
  const updatePosition = useCallback(() => {
    if (!isDragging.current) {
      // Apply momentum damping
      const damping = 0.95;
      velocityRef.current.x *= damping;
      velocityRef.current.y *= damping;

      // Update position based on velocity
      setViewState(prev => {
        const newState = {
          x: prev.x + velocityRef.current.x,
          y: prev.y + velocityRef.current.y,
          k: prev.k
        };
        applyTransform(newState);
        return newState;
      });

      // Continue animation if there's still velocity
      if (Math.abs(velocityRef.current.x) > 0.1 || Math.abs(velocityRef.current.y) > 0.1) {
        animationFrameRef.current = requestAnimationFrame(updatePosition);
      }
    }
  }, [applyTransform]);

  // Start countdown when user interacts
  const startCountdown = useCallback(() => {
    // Clear existing timers
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    setCountdown(5);

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set main timeout to re-enable auto-centering
    interactionTimeoutRef.current = setTimeout(() => {
      setIsUserInteracting(false);
      setCountdown(0);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 5000);
  }, []);

  // Handle mouse/touch start
  const handlePointerStart = useCallback((e: Event) => {
    // Don't prevent default for touch events initially - let HammerJS handle taps
    if (e.type === 'mousedown') {
      e.preventDefault();
      e.stopPropagation();
    }

    // For touch events, only start dragging after a small delay or movement
    if (e.type === 'touchstart') {
      // Store initial touch position but don't start dragging yet
      let clientX: number, clientY: number;
      if ('touches' in e && (e as TouchEvent).touches.length > 0) {
        clientX = (e as TouchEvent).touches[0].clientX;
        clientY = (e as TouchEvent).touches[0].clientY;
      } else {
        return;
      }

      lastPointerPos.current = { x: clientX, y: clientY };
      return;
    }

    // For mouse events, start dragging immediately
    isDragging.current = true;
    // Interrupt any ongoing D3 transition to avoid jump/hitch on first drag
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      const g = svg.select('g.map-container');
      if (g.node()) {
        g.interrupt();
      }
    }
    isTransitioning.current = false;
    setIsUserInteracting(true);
    startCountdown();

    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Reset velocity
    velocityRef.current = { x: 0, y: 0 };
    lastTimeRef.current = performance.now();

    // Get pointer position for mouse events
    const clientX = (e as MouseEvent).clientX;
    const clientY = (e as MouseEvent).clientY;

    lastPointerPos.current = { x: clientX, y: clientY };
    centeredOnExhibit.current = false;
  }, [startCountdown]);

  // Handle mouse/touch move
  const handlePointerMove = useCallback((e: Event) => {
    // For touch events, start dragging only if there's significant movement
    if (e.type === 'touchmove') {
      if (!isDragging.current) {
        // Check if we should start dragging based on movement
        let clientX: number, clientY: number;
        if ('touches' in e && (e as TouchEvent).touches.length > 0) {
          clientX = (e as TouchEvent).touches[0].clientX;
          clientY = (e as TouchEvent).touches[0].clientY;
        } else {
          return;
        }

        const deltaX = clientX - lastPointerPos.current.x;
        const deltaY = clientY - lastPointerPos.current.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Start dragging if movement is significant (more than 10px)
        if (distance > 10) {
          isDragging.current = true;
          setIsUserInteracting(true);
          startCountdown();

          // Interrupt any ongoing D3 transition
          if (svgRef.current) {
            const svg = d3.select(svgRef.current);
            const g = svg.select('g.map-container');
            if (g.node()) {
              g.interrupt();
            }
          }
          isTransitioning.current = false;

          // Cancel any ongoing animation
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }

          // Reset velocity
          velocityRef.current = { x: 0, y: 0 };
          lastTimeRef.current = performance.now();
        } else {
          return; // Don't process small movements as drags
        }
      }
    } else if (!isDragging.current) {
      return; // For mouse events, only process if already dragging
    }

    e.preventDefault();
    e.stopPropagation();

    // Get pointer position - handle both touch and mouse events
    let clientX: number, clientY: number;
    if ('touches' in e && (e as TouchEvent).touches.length > 0) {
      clientX = (e as TouchEvent).touches[0].clientX;
      clientY = (e as TouchEvent).touches[0].clientY;
    } else if ('changedTouches' in e && (e as TouchEvent).changedTouches.length > 0) {
      clientX = (e as TouchEvent).changedTouches[0].clientX;
      clientY = (e as TouchEvent).changedTouches[0].clientY;
    } else {
      clientX = (e as MouseEvent).clientX;
      clientY = (e as MouseEvent).clientY;
    }

    // Calculate delta
    const deltaX = clientX - lastPointerPos.current.x;
    const deltaY = clientY - lastPointerPos.current.y;

    // Calculate velocity for momentum
    const now = performance.now();
    const deltaTime = now - lastTimeRef.current;
    if (deltaTime > 0) {
      velocityRef.current.x = deltaX / deltaTime * 16; // Scale to 60fps
      velocityRef.current.y = deltaY / deltaTime * 16;
    }

    // Update position directly (no acceleration)
    setViewState(prev => {
      const newState = {
        x: prev.x + deltaX,
        y: prev.y + deltaY,
        k: prev.k
      };
      applyTransform(newState);
      return newState;
    });

    // Update last position and time
    lastPointerPos.current = { x: clientX, y: clientY };
    lastTimeRef.current = now;
    centeredOnExhibit.current = false;
  }, [applyTransform, startCountdown]);

  // Handle mouse/touch end
  const handlePointerEnd = useCallback((e: Event) => {
    // For touch events, always reset dragging state
    if (e.type === 'touchend' || e.type === 'touchcancel') {
      isDragging.current = false;
      centeredOnExhibit.current = false;
      return;
    }

    // For mouse events, only process if currently dragging
    if (!isDragging.current) return;

    e.preventDefault();
    e.stopPropagation();
    isDragging.current = false;
    centeredOnExhibit.current = false;
    // Start momentum animation if there's velocity
    if (Math.abs(velocityRef.current.x) > 0.1 || Math.abs(velocityRef.current.y) > 0.1) {
      animationFrameRef.current = requestAnimationFrame(updatePosition);
    }
  }, [updatePosition]);

  // Initialize smooth dragging and zoom
  const initializeZoom = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = svgRef.current;
    const container = containerRef.current;

    // mouse
    svg.addEventListener('mousedown', handlePointerStart);
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerEnd);

    // touch
    svg.addEventListener('touchstart', handlePointerStart, { passive: true });
    svg.addEventListener('touchmove', handlePointerMove, { passive: false, capture: true });
    svg.addEventListener('touchend', handlePointerEnd, { passive: true });
    svg.addEventListener('touchcancel', handlePointerEnd, { passive: true });

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      // sync to current transform if a transition was in flight
      const g = d3.select(svgRef.current!).select('g.map-container');
      g.interrupt();
      const tr = g.attr('transform') || '';
      const m = tr.match(/translate\(([-\d.]+),\s*([-\d.]+)\)\s*scale\(([-\d.]+)\)/);
      if (m) {
        const live = { x: parseFloat(m[1]), y: parseFloat(m[2]), k: parseFloat(m[3]) };
        setViewState(live);
        viewRef.current = live;
      }

      setIsUserInteracting(true);
      startCountdown();
      centeredOnExhibit.current = false;

      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const nextScale = Math.max(0.5, Math.min(3, viewRef.current.k * factor));

      // zoom towards canvas center
      zoomAtCanvasCenter(nextScale);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    // Initial positioning: use CURRENT K, not a fixed 1.0
    if (exhibits.length > 0 && !hasInitialCentered.current) {
      const exhibit = exhibits[currentTrack];
      if (exhibit) {
        const svgX = (exhibit.position.x / 100) * SVG_VIEWBOX.width;
        const svgY = (exhibit.position.y / 100) * SVG_VIEWBOX.height;
        const rect = container.getBoundingClientRect();
        const visibleHeight = Math.max(0, rect.height - bottomOverlayHeight);
        const cx = rect.width / 2;
        const cy = visibleHeight / 2;

        const { k: currentK } = getCurrentView(); // ⬅️ keep zoom if already set
        const initialTransform = { x: cx - svgX * currentK, y: cy - svgY * currentK, k: DEFAULT_ZOOM };

        setViewState(initialTransform);
        viewRef.current = initialTransform;
        applyTransform(initialTransform);
        hasInitialCentered.current = true;
      }
    }

    return () => {
      svg.removeEventListener('mousedown', handlePointerStart);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerEnd);
      svg.removeEventListener('touchstart', handlePointerStart as any);
      svg.removeEventListener('touchmove', handlePointerMove as any);
      svg.removeEventListener('touchend', handlePointerEnd as any);
      svg.removeEventListener('touchcancel', handlePointerEnd as any);
      container.removeEventListener('wheel', handleWheel as any);
    };
    // ⬇️ Only stable deps here; DO NOT include viewState or k
  }, [exhibits, currentTrack, bottomOverlayHeight, handlePointerStart, handlePointerMove, handlePointerEnd, applyTransform, startCountdown]);

  // Initialize HammerJS for touch gestures (simplified for tap detection only)
  const initializeHammer = useCallback(() => {
    if (!containerRef.current || hammerRef.current) return;

    hammerRef.current = new Hammer(containerRef.current);

    // Disable pan and other gestures that might interfere with dragging
    hammerRef.current.get('pan').set({ enable: false });
    hammerRef.current.get('pinch').set({ enable: false });
    hammerRef.current.get('rotate').set({ enable: false });
    hammerRef.current.get('swipe').set({ enable: false });

    // Only enable tap gestures for exhibit selection
    hammerRef.current.get('tap').set({
      enable: true,
      threshold: 10,
      time: 300
    });

    // Handle tap gestures for exhibit selection
    hammerRef.current.on('tap', (e) => {
      if (!svgRef.current) return;

      // Only process taps if we're not currently dragging
      if (isDragging.current) return;

      const svg = d3.select(svgRef.current);
      const point = d3.pointer(e.srcEvent, svg.node() as Element);

      // Find the closest exhibit marker
      const exhibitIndex = findClosestExhibit(point);
      if (exhibitIndex !== -1) {
        handleExhibitClick(exhibitIndex);
      }
    });
  }, []);

  // Find the closest exhibit to a point
  const findClosestExhibit = useCallback((point: [number, number]) => {
    let closestIndex = -1;
    let minDistance = Infinity;
    // Increase threshold for mobile devices for better touch targets
    const isMobile = window.innerWidth < 768;
    const threshold = isMobile ? 60 : 50; // Larger touch target on mobile

    exhibits.forEach((exhibit, index) => {
      const x = (exhibit.position.x / 100) * SVG_VIEWBOX.width;
      const y = (exhibit.position.y / 100) * SVG_VIEWBOX.height;
      const distance = Math.sqrt((point[0] - x) ** 2 + (point[1] - y) ** 2);

      if (distance < minDistance && distance < threshold) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, [exhibits]);

  // Handle exhibit click/tap
  const handleExhibitClick = useCallback((index: number) => {
    selectExhibit(index);
    centerOnExhibit(index);
  }, [selectExhibit]);

  // Center on exhibit with smooth transition
  const centerOnExhibit = useCallback((exhibitIndex: number) => {
    if (
      exhibitIndex < 0 ||
      exhibitIndex >= exhibits.length ||
      !svgRef.current ||
      !containerRef.current
    ) return;

    const exhibit = exhibits[exhibitIndex];
    if (!exhibit) return;

    isTransitioning.current = true;
    setIsUserInteracting(false);

    // stop any countdowns
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);

    // SVG coords for target
    const svgX = (exhibit.position.x / 100) * SVG_VIEWBOX.width;
    const svgY = (exhibit.position.y / 100) * SVG_VIEWBOX.height;

    const containerRect = containerRef.current.getBoundingClientRect();
    const visibleHeight = Math.max(0, containerRect.height - bottomOverlayHeight);
    const centerX = containerRect.width / 2;
    const centerY = visibleHeight / 2;

    // ⬅️ CRITICAL: keep the current zoom, don't alternate it
    const { k: currentK } = getCurrentView();

    const targetTransform: ViewState = {
      x: centerX - svgX * currentK,
      y: centerY - svgY * currentK,
      k: currentK
    };

    const svg = d3.select(svgRef.current);
    const g = svg.select('g.map-container');

    if (!g.node()) return;

    g.interrupt();
    g.transition()
      .duration(700)
      .ease(d3.easeCubicOut)
      .attr('transform', `translate(${targetTransform.x},${targetTransform.y}) scale(${targetTransform.k})`)
      .on('interrupt', () => {
        // read whatever was last painted; do NOT default to k=1
        const live = parseTransform(g.attr('transform'), viewRef.current);
        setViewState(live);
        viewRef.current = live;
        isTransitioning.current = false;
      })
      .on('end', () => {
        isTransitioning.current = false;
        setViewState(targetTransform);
        viewRef.current = targetTransform;
        if (isNavigationPressed && setIsNavigationPressed) setIsNavigationPressed(false);
        centeredOnExhibit.current = true;
      });

  }, [exhibits, bottomOverlayHeight, SVG_VIEWBOX.width, SVG_VIEWBOX.height, isNavigationPressed, setIsNavigationPressed]);

  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  const zoomAtCanvasCenter = (nextScale: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    // Canvas center in *container* coordinates
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    const prev = viewRef.current;

    // Convert canvas center to SVG coords under current transform
    const svgX = (cx - prev.x) / prev.k;
    const svgY = (cy - prev.y) / prev.k;

    // New translation that keeps the canvas center anchored
    const next = {
      x: cx - svgX * nextScale,
      y: cy - svgY * nextScale,
      k: nextScale,
    };

    applyTransform(next);
    setViewState(next);
    viewRef.current = next;
  };


  // Always fall back to the CURRENT view, never identity
  const parseTransform = (tr: string | null, fallback: ViewState): ViewState => {
    if (!tr) return fallback;

    // matches: translate(x,y) scale(k)
    const t = /translate\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/.exec(tr);
    const s = /scale\(\s*(-?\d+(?:\.\d+)?)\s*\)/.exec(tr);

    return {
      x: t ? +t[1] : fallback.x,
      y: t ? +t[2] : fallback.y,
      k: s ? +s[1] : fallback.k,
    };
  };

  // Initialize smooth dragging and HammerJS
  useEffect(() => {
    const cleanup = initializeZoom();
    initializeHammer();

    return () => {
      if (cleanup) cleanup();
      if (hammerRef.current) {
        hammerRef.current.destroy();
        hammerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [initializeZoom, initializeHammer]);

  // Initial centering on first exhibit
  useEffect(() => {
    if (exhibits.length > 0 && !hasInitialCentered.current) {
      // Wait for D3 and HammerJS to be fully initialized

      // clamp & set the starting zoom
      const startK = Math.max(MIN_K, Math.min(MAX_K, DEFAULT_ZOOM));
      viewRef.current = { ...viewRef.current, k: startK };
      setViewState(v => ({ ...v, k: startK }));

      setTimeout(() => {
        centerOnExhibit(currentTrack);
        hasInitialCentered.current = true;
      }, 800);

      // Fallback centering in case the first attempt doesn't work
      setTimeout(() => {
        if (!hasInitialCentered.current) {
          centerOnExhibit(currentTrack);
          hasInitialCentered.current = true;
        }
      }, 1500);
    }
  }, [exhibits.length, centerOnExhibit]);

  // Auto-center when currentTrack changes
  useEffect(() => {
    if (hasInitialCentered.current && !isTransitioning.current) {
      // Always center when navigation is pressed (prev/next buttons)
      if (isNavigationPressed) {
        centerOnExhibit(currentTrack);
      }
      // Otherwise, only center if user is not interacting
      else if (!isUserInteracting) {
        centerOnExhibit(currentTrack);
      }
    }
  }, [currentTrack, centerOnExhibit, isUserInteracting, isNavigationPressed]);

  // Auto-center when countdown expires
  useEffect(() => {
    if (hasInitialCentered.current && countdown === 0 && !isTransitioning.current) {
      // Only center if user is not interacting and navigation is not pressed
      if (!isUserInteracting && !isNavigationPressed) {
        setTimeout(() => {
          centerOnExhibit(currentTrack);
        }, 200);
      }
    }
  }, [isUserInteracting, countdown, currentTrack, centerOnExhibit, isNavigationPressed]);

  // Recenter when bottom overlay height changes
  useEffect(() => {
    if (hasInitialCentered.current && !isTransitioning.current) {
      // Always center when navigation is pressed (prev/next buttons)
      if (isNavigationPressed) {
        centerOnExhibit(currentTrack);
      }
      // Otherwise, only center if user is not interacting
      else if (!isUserInteracting) {
        centerOnExhibit(currentTrack);
      }
    }
  }, [bottomOverlayHeight, currentTrack, centerOnExhibit, isUserInteracting, isNavigationPressed]);

  // Reset navigation pressed state after timeout
  useEffect(() => {
    if (isNavigationPressed && setIsNavigationPressed) {
      // Clear any existing timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        centerOnExhibit(currentTrack);
      }
      // Set timeout to reset navigation state
      navigationTimeoutRef.current = setTimeout(() => {
        setIsNavigationPressed(false);
      }, 100); // 3 seconds timeout for navigation
    }

    // Cleanup timeout on unmount
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [isNavigationPressed, setIsNavigationPressed]);

  // Expose resetUserInteraction function to parent
  useEffect(() => {
    if (resetUserInteraction) {
      resetUserInteraction.current = () => {
        setIsUserInteracting(false);
        setCountdown(0);
        // Clear any existing timeouts
        if (interactionTimeoutRef.current) {
          clearTimeout(interactionTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    }
  }, [resetUserInteraction]);

  // Calculate responsive marker size
  const getMarkerSize = () => {
    const isMobile = window.innerWidth < 768;
    return isMobile ? 32 : 28;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      style={{ backgroundColor: 'var(--color-primary)', touchAction: 'none' }}
    >
      <svg
        ref={svgRef}
        width={containerSize.width}
        height={containerSize.height}
        style={{ display: 'block' }}
      >
        <g className="map-container">
          {/* Museum map background */}
          <image
            href="/map.svg"
            width={SVG_VIEWBOX.width}
            height={SVG_VIEWBOX.height}
            style={{ pointerEvents: 'none' }}
          />

          {/* Exhibit markers */}
          {exhibits.map((exhibit, index) => {
            const markerSize = getMarkerSize();
            const x = (exhibit.position.x / 100) * SVG_VIEWBOX.width;
            const y = (exhibit.position.y / 100) * SVG_VIEWBOX.height;
            const isCurrentExhibit = currentTrack === index;
            const isVisited = visitedExhibits.has(index);

            return (
              <g key={exhibit.id}>
                {/* Touch target (larger invisible circle) */}
                <circle
                  cx={x}
                  cy={y}
                  r={Math.max(markerSize, window.innerWidth < 768 ? 40 : 30)}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleExhibitClick(index)}
                />

                {/* Visible marker */}
                <circle
                  cx={x}
                  cy={y}
                  r={markerSize / 2}
                  fill={isCurrentExhibit ? 'var(--dot-current-fill)' : 'var(--dot-regular-fill)'}
                  stroke={isCurrentExhibit ? 'var(--dot-current-stroke)' : 'var(--dot-regular-stroke)'}
                  strokeWidth="3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleExhibitClick(index)}
                />

                {/* Exhibit number */}
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="white"
                  fontSize={markerSize * 0.5}
                  fontWeight="bold"
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}
                >
                  {exhibit.order}
                </text>

                {/* Current exhibit highlight ring */}
                {isCurrentExhibit && (
                  <circle
                    cx={x}
                    cy={y}
                    r={markerSize / 2 + 4}
                    fill="none"
                    stroke="var(--dot-highlight-ring)"
                    strokeWidth="4"
                    style={{
                      animation: 'pulse 2s infinite',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Status indicator */}
      {isUserInteracting && (
        <div className="absolute top-4 right-4 backdrop-blur-sm px-3 py-1 rounded-lg text-sm z-20 text-white" style={{ backgroundColor: 'var(--color-capsule)' }}>
          {t('museumMap.autoCenterDisabled')} {countdown > 0 && `(${countdown}s)`}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        /* Improve touch handling */
        svg {
          touch-action: none !important;
        }
        
        /* Prevent text selection but allow touch events */
        svg * {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default MuseumMapSVG;
